import { useStore } from 'effector-react';
import { useEffect, useMemo } from 'react';
import FilterLine from 'components/FilterLine';
import FilterResult from 'components/FilterResult';
import LoadMoreButton from 'components/LoadMoreButton';
import NewGatewayButton from 'components/NewGatewayBlock/NewGatewayButton';
import NoContent from 'components/NoContent';
import {
	clearPiiFilter,
	piiFilterToArray,
	piiFilterWithGroupsStore,
} from 'components/PiiGlobalFilterV2/model';
import Preloader from 'components/Preloader';
import ResetFilters from 'components/ResetFilters';
import EnhancedTableHead, { TOrder } from 'components/table/EnhancedTableHead';
import GridBody from 'components/table/GridBody';
import GridTable from 'components/table/GridTable';
import { Typo } from 'components/typography/Typo';
import { DatabaseDetails, DatabaseItem } from 'models/databases/dto';
import { databasesModel } from 'models/databases/model';
import { databaseInstancesStore } from 'models/databases/store';
import { PageParamsConfig, usePageParams } from 'services/pageParams';
import { UniversalSearch } from 'views/common/UniversalSearch';
import DataStorage from 'views/DataStorages';
import tableConfig from './config';
import styles from './index.module.css';
import { TableRow } from './TableRow';

const pageConfig = {
	'instance-ids': {
		type: 'numberArray',
		persistence: 'session',
	},
	'data-types': {
		type: 'numberArray',
		persistence: 'session', // TODO implement 'global' in actuality
	},
	nonempty: {
		type: 'boolean',
		persistence: 'session', // TODO implement 'global' in actuality
	},
	search: {
		type: 'string',
		persistence: 'session',
	},
	sort: {
		type: 'sort',
		persistence: 'session',
	},
} satisfies PageParamsConfig;

export function DatabasesTable() {
	const state = useStore(databasesModel.store);
	const piiFilterWithGroups = useStore(piiFilterWithGroupsStore);
	const databaseInstances = useStore(databaseInstancesStore);

	// These addition tables the only one way to show DB instances where crawler wasn't attached
	// because we don't have filters by instances or separate page/view for them
	const nonExistedTables = useMemo(() => {
		return databaseInstances
			.filter((instance) => !instance.is_crawler_attached)
			.map(
				(instance): DatabaseDetails => ({
					data_types: [],
					description: '',
					id: instance.id,
					instance_id: instance.id,
					last_checked: 0,
					name: 'N/A',
					owner: '',
					sensitivity: 'N/A',
					size: 0,
					status: '',
					tables_count: 0,
				})
			);
	}, [databaseInstances]);

	const [pageParams, setPageParams] = usePageParams(pageConfig, 'databases');

	useEffect(() => {
		setPageParams({
			...pageParams,
			'data-types': piiFilterWithGroups.dataTypes,
			nonempty: piiFilterWithGroups.nonEmpty,
		});
	}, [piiFilterWithGroups]);

	useEffect(() => {
		const { sort, 'instance-ids': instance_ids, search } = pageParams;

		const dataTypes = piiFilterToArray(piiFilterWithGroups);
		const paramsForFx = {
			sort: { orderBy: sort.value as keyof DatabaseItem, order: sort.operator },
			instance_ids,
			search,
			'data-types': dataTypes,
		};

		databasesModel.fetchFx(paramsForFx);
	}, [pageParams]);

	// Clean up model on unmount
	useEffect(() => {
		return () => {
			databasesModel.resetFx();
		};
	}, []);

	function onSortUpdate(property: keyof DatabaseItem) {
		const { sort } = pageParams;

		const operator: TOrder = sort.value === property && sort.operator === 'asc' ? 'desc' : 'asc';
		const newParams = { sort: { operator, value: property } };

		setPageParams({ ...pageParams, ...newParams });
	}

	function onUniversalSearchUpdate(search: string) {
		setPageParams({ ...pageParams, search });
	}

	function onResetFilters() {
		const newParams = {
			search: '',
			'data-types': [],
			'instance-ids': [],
		};

		setPageParams({ ...pageParams, ...newParams });
		clearPiiFilter();
	}

	const { status, data, total, total_filtered, hasMoreData } = state;

	const hasFilter =
		pageParams.search !== '' ||
		pageParams['data-types'].length > 0 ||
		pageParams['instance-ids'].length > 0;

	return (
		<DataStorage>
			<DataStorage.Header breadcrumbsFinalStep="SQL Databases" />

			<UniversalSearch value={pageParams.search} onChange={onUniversalSearchUpdate} />

			<FilterLine
				config={['dataTypes']}
				values={{
					dataTypes: pageParams['data-types'],
				}}
				onChange={() => {}}
			/>

			<FilterResult
				entityLabel="database"
				loading={status === 'loading'}
				total={total}
				totalFiltered={total_filtered}
			/>

			<GridTable dataTest="database-table">
				<EnhancedTableHead
					config={tableConfig}
					order={pageParams.sort.operator}
					orderBy={pageParams.sort.value}
					onRequestSort={onSortUpdate}
					rowClassname={styles.rowContainer}
				/>

				<Preloader isLoading={status === 'initial' || status === 'loading'}>
					<GridBody data-test="database-list">
						<>
							{data.length || nonExistedTables.length ? (
								[...data, ...nonExistedTables].map((db) => (
									<TableRow db={db} search={pageParams.search} key={db.id} />
								))
							) : hasFilter ? (
								<ResetFilters onReset={onResetFilters} />
							) : (
								<NoContent type="databases" className={styles.rowContainer}>
									<div className={styles.emptyContainer}>
										<Typo variant="D/Regular/Body-S" color="secondary">
											No available analytics or no Sensors set up yet.
										</Typo>

										<NewGatewayButton isDAR />
									</div>
								</NoContent>
							)}

							<LoadMoreButton
								show={hasMoreData}
								loading={status === 'loadingMore'}
								request={databasesModel.fetchMoreFx}
							/>
						</>
					</GridBody>
				</Preloader>
			</GridTable>
		</DataStorage>
	);
}
